import { useState, useEffect, useRef, useCallback } from 'react'
import { BASE_URL } from '../../apiConfig'
import { useAuth } from '../../context/AuthProvider'
import { useLanguage } from '../../context/LanguageProvider'
import {
  Terminal, Search, Filter, ChevronDown, ChevronUp,
  User, Package, ShoppingCart, Truck, Shield, Settings,
  CreditCard, Bell, MessageSquare, Ban, Zap, RefreshCw,
  Clock, X, ChevronLeft, ChevronRight, Eye, EyeOff,
  Globe, Monitor, ExternalLink, CheckCircle
} from 'lucide-react'

const ACTION_CONFIG = {
  'user.login':            { icon: Shield, color: 'text-emerald-400', category: 'auth' },
  'user.logout':           { icon: Shield, color: 'text-slate-400', category: 'auth' },
  'user.registered':       { icon: User, color: 'text-emerald-400', category: 'user' },
  'user.staff_registered': { icon: User, color: 'text-emerald-400', category: 'user' },
  'user.deleted':          { icon: User, color: 'text-red-400', category: 'user' },
  'user.seller_status_changed': { icon: User, color: 'text-yellow-400', category: 'user' },
  'user.profile_updated':  { icon: User, color: 'text-blue-400', category: 'user' },
  'user.password_changed': { icon: Shield, color: 'text-orange-400', category: 'auth' },
  'user.saller_updated':   { icon: User, color: 'text-blue-400', category: 'user' },
  'user.saller_profile_updated': { icon: User, color: 'text-blue-400', category: 'user' },
  'user.admin_profile_updated': { icon: User, color: 'text-blue-400', category: 'user' },
  'user.confirmator_profile_updated': { icon: User, color: 'text-blue-400', category: 'user' },
  'order.created':         { icon: ShoppingCart, color: 'text-emerald-400', category: 'order' },
  'order.cart_created':    { icon: ShoppingCart, color: 'text-emerald-400', category: 'order' },
  'order.updated':         { icon: ShoppingCart, color: 'text-blue-400', category: 'order' },
  'order.deleted':         { icon: ShoppingCart, color: 'text-red-400', category: 'order' },
  'order.confirmed':       { icon: CheckCircle, color: 'text-teal-400', category: 'order' },
  'order.status_changed':  { icon: RefreshCw, color: 'text-cyan-400', category: 'order' },
  'order.livreur_status_changed': { icon: Truck, color: 'text-cyan-400', category: 'delivery' },
  'order.sent_to_delivery':{ icon: Truck, color: 'text-purple-400', category: 'delivery' },
  'order.delivery_sync':   { icon: RefreshCw, color: 'text-slate-400', category: 'delivery' },
  'order.scanned':         { icon: Package, color: 'text-emerald-400', category: 'order' },
  'order.imported':        { icon: Package, color: 'text-emerald-400', category: 'order' },
  'order.ozon_status_changed': { icon: RefreshCw, color: 'text-blue-400', category: 'order' },
  'order.api_created':     { icon: ShoppingCart, color: 'text-green-400', category: 'order' },
  'webhook.turboliv':      { icon: Zap, color: 'text-yellow-400', category: 'webhook' },
  'webhook.irsaliyat':     { icon: Zap, color: 'text-yellow-400', category: 'webhook' },
  'webhook.sift':          { icon: Zap, color: 'text-yellow-400', category: 'webhook' },
  'webhook.nexship':       { icon: Zap, color: 'text-yellow-400', category: 'webhook' },
  'webhook.nexship.v2':    { icon: Zap, color: 'text-yellow-400', category: 'webhook' },
  'product.created':       { icon: Package, color: 'text-emerald-400', category: 'product' },
  'product.updated':       { icon: Package, color: 'text-blue-400', category: 'product' },
  'product.deleted':       { icon: Package, color: 'text-red-400', category: 'product' },
  'product.pin_toggled':   { icon: Package, color: 'text-slate-400', category: 'product' },
  'product.shopify_sync':  { icon: Zap, color: 'text-purple-400', category: 'sync' },
  'product.youcan_sync':   { icon: Zap, color: 'text-purple-400', category: 'sync' },
  'product.shopify_bulk_sync': { icon: Zap, color: 'text-purple-400', category: 'sync' },
  'product.youcan_bulk_sync': { icon: Zap, color: 'text-purple-400', category: 'sync' },
  'role.created':          { icon: Shield, color: 'text-emerald-400', category: 'role' },
  'role.updated':          { icon: Shield, color: 'text-blue-400', category: 'role' },
  'role.deleted':          { icon: Shield, color: 'text-red-400', category: 'role' },
  'category.created':      { icon: Package, color: 'text-emerald-400', category: 'category' },
  'category.updated':      { icon: Package, color: 'text-blue-400', category: 'category' },
  'category.deleted':      { icon: Package, color: 'text-red-400', category: 'category' },
  'variant.created':       { icon: Package, color: 'text-emerald-400', category: 'variant' },
  'variant.updated':       { icon: Package, color: 'text-blue-400', category: 'variant' },
  'variant.deleted':       { icon: Package, color: 'text-red-400', category: 'variant' },
  'fournisseur.created':   { icon: User, color: 'text-emerald-400', category: 'supplier' },
  'fournisseur.updated':   { icon: User, color: 'text-blue-400', category: 'supplier' },
  'fournisseur.deleted':   { icon: User, color: 'text-red-400', category: 'supplier' },
  'annonce.created':       { icon: Bell, color: 'text-emerald-400', category: 'annonce' },
  'annonce.updated':       { icon: Bell, color: 'text-blue-400', category: 'annonce' },
  'annonce.deleted':       { icon: Bell, color: 'text-red-400', category: 'annonce' },
  'reclamation.created':   { icon: MessageSquare, color: 'text-yellow-400', category: 'reclamation' },
  'reclamation.messages_seen': { icon: MessageSquare, color: 'text-slate-400', category: 'reclamation' },
  'blacklist.added':       { icon: Ban, color: 'text-red-400', category: 'blacklist' },
  'blacklist.removed':     { icon: Ban, color: 'text-emerald-400', category: 'blacklist' },
  'invoice.saller_paid':   { icon: CreditCard, color: 'text-emerald-400', category: 'invoice' },
  'invoice.confirmator_paid': { icon: CreditCard, color: 'text-emerald-400', category: 'invoice' },
  'invoice.livreur_paid':  { icon: CreditCard, color: 'text-emerald-400', category: 'invoice' },
  'invoice.saller_auto_generated': { icon: CreditCard, color: 'text-purple-400', category: 'invoice' },
  'invoice.confirmator_auto_generated': { icon: CreditCard, color: 'text-purple-400', category: 'invoice' },
  'invoice.livreur_auto_generated': { icon: CreditCard, color: 'text-purple-400', category: 'invoice' },
  'config.updated':        { icon: Settings, color: 'text-blue-400', category: 'config' },
  'payment_system.updated': { icon: CreditCard, color: 'text-blue-400', category: 'payment' },
  'delivery_company.updated': { icon: Truck, color: 'text-blue-400', category: 'delivery' },
  'integration.oauth_initiated': { icon: Zap, color: 'text-purple-400', category: 'integration' },
  'integration.updated':   { icon: Zap, color: 'text-blue-400', category: 'integration' },
  'api_key.generated':     { icon: Zap, color: 'text-purple-400', category: 'api_key' },
  'api_key.webhook_url':   { icon: Zap, color: 'text-blue-400', category: 'api_key' },
  'api_key.webhook_status':{ icon: Zap, color: 'text-cyan-400', category: 'api_key' },
  'api_key.revoked':       { icon: Zap, color: 'text-red-400', category: 'api_key' },
  'product.created.external': { icon: Package, color: 'text-green-400', category: 'product' },
}

const getCategoryLabel = (action) => {
  const config = ACTION_CONFIG[action]
  if (!config) return 'system'
  return config.category
}

const getCategoryColor = (action) => {
  const config = ACTION_CONFIG[action]
  if (!config) return 'text-slate-400'
  return config.color
}

const isMetaKey = (key) => key === 'ip_address' || key === 'user_agent'

const getDetailLabel = (t, key) => {
  const label = t('activity_logs.details.' + key)
  return label !== ('activity_logs.details.' + key) ? label : key
}

// Actions whose path collides with a parent string key cannot be nested in the
// dot-notation translator, so resolve their labels here per locale.
const ACTION_LABEL_FALLBACKS = {
  fr: {
    'product.created.external': 'Produit créé via API',
    'webhook.nexship.v2': 'Webhook Nexship v2',
  },
  ar: {
    'product.created.external': 'منتج تم إنشاؤه عبر API',
    'webhook.nexship.v2': 'Webhook Nexship v2',
  },
}

const getActionLabel = (locale, t, action) => {
  const key = 'activity_logs.actions.' + action
  const label = t(key)
  if (label !== key) return label
  return (ACTION_LABEL_FALLBACKS[locale] && ACTION_LABEL_FALLBACKS[locale][action]) || action
}

export default function ActivityLogsPage() {
  const { token } = useAuth()
  const { t, locale } = useLanguage()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [actions, setActions] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const scrollRef = useRef(null)
  const [autoRefresh, setAutoRefresh] = useState(false)

  const fetchLogs = useCallback(async (page = 1, isLoadMore = false) => {
    if (!isLoadMore) setLoading(true)
    else setIsFetchingMore(true)

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
      })
      if (searchTerm) params.append('search', searchTerm)
      if (actionFilter) params.append('action', actionFilter)

      const response = await fetch(`${BASE_URL}/api/activity-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()

      if (data.status && data.data) {
        if (isLoadMore) {
          setLogs(prev => [...prev, ...data.data.data])
        } else {
          setLogs(data.data.data)
        }
        setCurrentPage(data.data.current_page)
        setTotalPages(data.data.last_page)
        setTotalItems(data.data.total)
      }
    } catch {
      // silent
    } finally {
      setLoading(false)
      setIsFetchingMore(false)
    }
  }, [token, searchTerm, actionFilter])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    fetch(`${BASE_URL}/api/activity-logs/actions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.status && data.data) setActions(data.data)
      })
      .catch(() => {})
  }, [token])

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return
    const timer = setInterval(() => fetchLogs(), 10000)
    return () => clearInterval(timer)
  }, [autoRefresh, fetchLogs])

  // Infinite scroll
  useEffect(() => {
    if (!scrollRef.current) return
    const el = scrollRef.current
    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        if (!loading && !isFetchingMore && currentPage < totalPages) {
          fetchLogs(currentPage + 1, true)
        }
      }
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [loading, isFetchingMore, currentPage, totalPages, fetchLogs])

  const formatTime = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  }

  const handleSearch = () => {
    setCurrentPage(1)
    fetchLogs(1)
  }

  return (
    <div className="min-h-[89vh]">
      <div className="container mx-auto px-4 pt-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Terminal className="w-7 h-7 text-emerald-400" />
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                {t('activity_logs.title') || 'Activity Logs'}
              </span>
            </h1>
            <p className="text-xs text-gray-500 mt-1 font-mono">
              {t('activity_logs.subtitle') || 'System activity monitoring console'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono transition-all cursor-pointer ${
                autoRefresh
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
              }`}
            >
              <RefreshCw className={`w-3 h-3 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'LIVE' : 'AUTO'}
            </button>
            <span className="text-[10px] text-gray-600 font-mono">
              {totalItems} {t('activity_logs.entries') || 'entries'}
            </span>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-3 mb-2">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('activity_logs.search') || 'Search logs...'}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-400 hover:text-gray-200 hover:border-gray-600 transition-colors cursor-pointer"
              >
                <Filter className="w-3.5 h-3.5" />
                {t('activity_logs.filters') || 'Filters'}
                {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
              >
                {t('activity_logs.search_btn') || 'Search'}
              </button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-800">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setActionFilter(''); setCurrentPage(1) }}
                  className={`px-3 py-1 rounded-full text-[10px] font-mono transition-all cursor-pointer ${
                    !actionFilter
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-gray-800 text-gray-500 border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  ALL
                </button>
                {actions.map(action => (
                  <button
                    key={action}
                    onClick={() => { setActionFilter(action); setCurrentPage(1) }}
                    className={`px-3 py-1 rounded-full text-[10px] font-mono transition-all cursor-pointer ${
                      actionFilter === action
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-gray-800 text-gray-500 border border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {getActionLabel(locale, t, action)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Terminal Window */}
        <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden shadow-2xl">
          {/* Terminal Title Bar */}
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-900 border-b border-gray-800">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>
            <span className="text-[10px] text-gray-500 font-mono ml-2">
              activity-logs@platform:~$
            </span>
          </div>

          {/* Log Content */}
          <div
            ref={scrollRef}
            className="max-h-[calc(100vh-45vh)] overflow-y-auto overflow-x-auto custom-scrollbar"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#374151 #111827' }}
          >
            {loading && logs.length === 0 ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center gap-2 text-emerald-400 font-mono text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('activity_logs.loading') || 'Loading activity logs...'}
                </div>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center">
                <Terminal className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-600 font-mono text-sm">
                  {t('activity_logs.no_logs') || 'No activity logs found'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800/50">
                {logs.map((log, i) => {
                  const config = ACTION_CONFIG[log.action] || { icon: Terminal, color: 'text-gray-400' }
                  const Icon = config.icon
                  const descColor = getCategoryColor(log.action)

                  const isExpanded = expandedId === log.id
                  const details = log.details && typeof log.details === 'object' ? log.details : {}
                  const metaKeys = Object.keys(details).filter(isMetaKey)
                  const dataKeys = Object.keys(details).filter(k => !isMetaKey(k))

                  return (
                    <div
                      key={log.id || i}
                      className="px-4 py-0 hover:bg-gray-900/80 transition-colors group cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : log.id)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Timestamp */}
                        <div className="flex-shrink-0 flex items-center gap-1">
                          <span className="text-[10px] text-gray-600 font-mono">
                            {formatDate(log.created_at)}
                          </span>
                          <span className="text-[10px] text-gray-600 font-mono tabular-nums">
                            {formatTime(log.created_at)}
                          </span>
                        </div>

                        {/* Icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                        </div>

                        {/* Content */}
                        <div className="flex gap-3 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Action badge */}
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider ${
                              descColor.replace('text-', 'bg-').replace('400', '500/10')
                            } ${descColor}`}>
                              {getActionLabel(locale, t, log.action)}
                            </span>

                            {/* User badge */}
                            <span className="text-[10px] text-gray-600 font-mono">
                              <span className="text-gray-500">by</span>{' '}
                              <span className="text-gray-300">{log.user_name}</span>
                              <span className="text-gray-700 mx-0.5">({log.user_role})</span>
                            </span>
                          </div>

                          {/* Description */}
                          <p className={`text-xs mt-0.5 font-mono ${descColor} opacity-90`}>
                            {log.description}
                          </p>

                          {/* IP & User Agent badges */}
                          {metaKeys.length > 0 && !isExpanded && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              {details.ip_address && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-800/50 text-[9px] font-mono text-gray-500">
                                  <Globe className="w-2.5 h-2.5" />
                                  {details.ip_address}
                                </span>
                              )}
                              {details.user_agent && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-800/50 text-[9px] font-mono text-gray-500 truncate max-w-[200px]">
                                  <Monitor className="w-2.5 h-2.5 flex-shrink-0" />
                                  {details.user_agent}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Expand toggle */}
                        <div className="flex-shrink-0 flex items-center pt-1">
                          {dataKeys.length > 0 && (
                            <span className="text-gray-600 hover:text-gray-400 transition-colors">
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details Panel */}
                      {isExpanded && dataKeys.length > 0 && (
                        <div className="mt-2 ml-9 pl-3 border-l-2 border-emerald-500/20">
                          <div className="bg-gray-900/60 rounded-lg border border-gray-800/50 p-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {dataKeys.map((key) => {
                                const val = details[key]
                                const displayVal = typeof val === 'object' ? JSON.stringify(val, null, 1) : String(val)
                                const isLong = displayVal.length > 60

                                return (
                                  <div key={key} className="flex flex-col gap-0.5">
                                    <span className="text-[9px] font-mono font-semibold text-gray-600 uppercase tracking-wider">
                                      {getDetailLabel(t, key)}
                                    </span>
                                    <span className={`font-mono ${isLong ? 'text-[10px]' : 'text-[11px]'} text-gray-300 break-all ${isLong ? 'max-h-20 overflow-y-auto' : ''}`}>
                                      {displayVal}
                                    </span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Loading more indicator */}
                {isFetchingMore && (
                  <div className="p-4 text-center">
                    <div className="inline-flex items-center gap-2 text-emerald-400 font-mono text-xs">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      {t('activity_logs.loading_more') || 'Loading more...'}
                    </div>
                  </div>
                )}

                {/* End of logs */}
                {!isFetchingMore && currentPage >= totalPages && logs.length > 0 && (
                  <div className="p-4 text-center">
                    <span className="text-[10px] text-gray-700 font-mono">
                      --- {t('activity_logs.end') || 'END OF LOGS'} ---
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Terminal Footer */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-t border-gray-800">
            <span className="text-[10px] text-gray-600 font-mono">
              page {currentPage}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => currentPage > 1 && fetchLogs(currentPage - 1)}
                disabled={currentPage <= 1}
                className="p-1 text-gray-600 hover:text-gray-400 disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => currentPage < totalPages && fetchLogs(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-1 text-gray-600 hover:text-gray-400 disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
